import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CheckCircle, ShoppingBag, Package, Copy, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export default function OrderConfirmation() {
  const { shopSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orderId = location.state?.orderId;

  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // If page is opened directly without order state, send user back safely
    if (!orderId) {
      navigate(`/shop/${shopSlug}`);
      return;
    }

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const redirectTimer = setTimeout(() => {
      navigate(`/shop/${shopSlug}`);
    }, 10000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(redirectTimer);
    };
  }, [orderId, navigate, shopSlug]);
  
  
  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    toast.success('Order ID copied!');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="order-confirmation-page">
      <Card className="max-w-md w-full border-border/50 overflow-hidden">
        {/* Success Header */}
        <div className="bg-secondary/10 p-8 text-center">
          <div className="h-20 w-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4 animate-bounce">
            <HeartHandshake className="h-10 w-10 text-secondary" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Order Placed Successfully!</h1>
          <p className="text-muted-foreground">
            Your order has been placed successfully and the seller will confirm your order soon.
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Redirecting to the store in {countdown} second{countdown !== 1 ? 's' : ''}...
          </p>
        </div>

        <CardContent className="p-6 space-y-6">
          {/* Order ID */}
          {orderId && (
            <div className="bg-muted rounded-xl p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">Order ID</p>
              <div className="flex items-center justify-center gap-2">
                <p className="font-mono font-bold text-lg">#{orderId.substring(0, 8)}</p>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8"
                  onClick={copyOrderId}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="space-y-3">
            <h3 className="font-heading font-semibold">What happens next?</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <p className="text-muted-foreground">The seller will review your order and confirm it shortly</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <p className="text-muted-foreground">You'll receive an email notification once the seller responds</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <p className="text-muted-foreground">Track delivery status in the Orders tab</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-2">
            {user && user.role === 'customer' ? (
              <Link to={`/customer/orders?store=${shopSlug}`} className="block">
                <Button className="w-full rounded-full gap-2" data-testid="go-to-orders-btn">
                  <Package className="h-4 w-4" />
                  Go to My Orders
                </Button>
              </Link>
            ) : (
              <Link to={`/customer/login?redirect=/customer/orders?store=${shopSlug}`} className="block">
                <Button className="w-full rounded-full gap-2" data-testid="login-to-track-btn">
                  <Package className="h-4 w-4" />
                  Login to Track Order
                </Button>
              </Link>
            )}
            
            <Link to={`/shop/${shopSlug}`} className="block">
              <Button variant="outline" className="w-full rounded-full gap-2" data-testid="continue-shopping-btn">
                <ShoppingBag className="h-4 w-4" />
                Continue Shopping
              </Button>
            </Link>
          </div>

          {/* Email Notice */}
          <p className="text-xs text-center text-muted-foreground">
            A confirmation email has been sent to your registered email address
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
